import { useState, useCallback, useEffect } from 'react';

export interface CartVehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  daily_rate: number;
  primary_photo?: string;
}

interface CartState {
  vehicles: CartVehicle[];
  pickupDate: string;
  returnDate: string;
  pickupLocation: string;
}

const CART_KEY = 'purplemine_cart';

function loadCart(): CartState {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { vehicles: [], pickupDate: '', returnDate: '', pickupLocation: '' };
}

function saveCart(cart: CartState) {
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
}

// Singleton-like pattern: all components share the same cart state via localStorage
// We use a custom event to sync across components
const cartListeners = new Set<() => void>();

function notifyListeners() {
  cartListeners.forEach(fn => fn());
}

export function useCart() {
  const [cart, setCart] = useState<CartState>(loadCart);

  // Listen for cross-component updates
  useEffect(() => {
    const handler = () => setCart(loadCart());
    cartListeners.add(handler);
    return () => { cartListeners.delete(handler); };
  }, []);

  const update = useCallback((next: CartState) => {
    saveCart(next);
    setCart(next);
    notifyListeners();
  }, []);

  const addVehicle = useCallback((v: CartVehicle) => {
    const current = loadCart();
    if (current.vehicles.some(x => x.id === v.id)) return; // already added
    const next = { ...current, vehicles: [...current.vehicles, v] };
    update(next);
  }, [update]);

  const removeVehicle = useCallback((id: string) => {
    const current = loadCart();
    const next = { ...current, vehicles: current.vehicles.filter(v => v.id !== id) };
    update(next);
  }, [update]);

  const setDates = useCallback((pickupDate: string, returnDate: string) => {
    const current = loadCart();
    const next = { ...current, pickupDate, returnDate };
    update(next);
  }, [update]);

  const setLocation = useCallback((pickupLocation: string) => {
    const current = loadCart();
    const next = { ...current, pickupLocation };
    update(next);
  }, [update]);

  const clearCart = useCallback(() => {
    update({ vehicles: [], pickupDate: '', returnDate: '', pickupLocation: '' });
  }, [update]);

  const hasVehicle = useCallback((id: string) => {
    return cart.vehicles.some(v => v.id === id);
  }, [cart.vehicles]);

  return {
    cart,
    addVehicle,
    removeVehicle,
    setDates,
    setLocation,
    clearCart,
    hasVehicle,
  };
}
